import React, { useState } from "react"
import { Testimonial } from "../../lib/testimonials"
import { TestimonialCard } from "../ui/testimonial"
import { ChevronLeft, ChevronRight, Quote } from "lucide-react"
import { Button } from "../ui/button"
import { EventCategory } from "../../lib/analytics"
import { safeTrackEvent } from "../../lib/analytics-utils"
import { useLocation } from "react-router-dom"

// Create a wrapper for TestimonialCard that doesn't use ScrollReveal
function TestimonialCardStatic(props: { testimonial: Testimonial }) {
  return (
    <div className="bg-background/95 backdrop-blur-sm rounded-xl p-8 border border-primary/10 shadow-sm">
      <div className="space-y-6">
        <Quote className="w-12 h-12 text-primary/20" strokeWidth={1} />
        <p 
          className="text-lg italic whitespace-pre-line"
          dangerouslySetInnerHTML={{ 
            __html: props.testimonial.fullQuote || props.testimonial.quote 
          }}
        />
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-primary font-semibold">
              {props.testimonial.name 
                ? props.testimonial.name.split(' ')[0][0] + props.testimonial.name.split(' ')[1][0] 
                : props.testimonial.role.split(' ').map(word => word[0]).join('')}
            </span>
          </div>
          <div>
            <p className="font-medium">
              {props.testimonial.name || props.testimonial.role}
            </p>
            <p className="text-sm text-muted-foreground">
              {props.testimonial.role && props.testimonial.name ? props.testimonial.role + ", " : ""}
              {props.testimonial.company}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

interface TestimonialCarouselProps {
  testimonials: Testimonial[]
  className?: string
}

export function TestimonialCarousel({ testimonials, className = "" }: TestimonialCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const location = useLocation();
  const pageName = location.pathname === "/" ? "home" : location.pathname.replace(/^\/|\/$/g, '').replace(/\//g, '_');

  const moveNext = () => {
    setCurrentIndex((current) => (current + 1) % testimonials.length)
    
    safeTrackEvent({
      category: EventCategory.Engagement,
      action: 'click',
      label: `engagement_${pageName}_testimonial_next`,
      context: {
        testimonialId: testimonials[currentIndex].id,
        carouselPosition: currentIndex,
        sourcePage: location.pathname
      }
    });
  }

  const movePrev = () => {
    setCurrentIndex((current) => 
      current === 0 ? testimonials.length - 1 : current - 1
    )
    
    safeTrackEvent({
      category: EventCategory.Engagement,
      action: 'click',
      label: `engagement_${pageName}_testimonial_prev`,
      context: {
        testimonialId: testimonials[currentIndex].id,
        carouselPosition: currentIndex,
        sourcePage: location.pathname
      }
    });
  }

  if (testimonials.length === 0) return null
  
  return (
    <div className={className}>
      <div className="relative">
        <div className="overflow-hidden">
          <div 
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id}
                className="w-full flex-shrink-0 px-4"
              >
                <TestimonialCardStatic testimonial={testimonial} />
              </div>
            ))}
          </div>
        </div>
        
        {testimonials.length > 1 && (
          <>
            <div className="absolute top-1/2 left-0 -translate-y-1/2 z-20">
              <Button 
                variant="outline" 
                size="icon" 
                className="h-10 w-10 rounded-full bg-background shadow-md"
                onClick={movePrev}
                aria-label="Previous testimonial"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="absolute top-1/2 right-0 -translate-y-1/2 z-20">
              <Button 
                variant="outline" 
                size="icon" 
                className="h-10 w-10 rounded-full bg-background shadow-md"
                onClick={moveNext}
                aria-label="Next testimonial"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
} 